import { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/Button'
import { EmptyState, ErrorState, LoadingState, Notice } from '@/components/ui/Feedback'
import { NativeSelect } from '@/components/ui/NativeSelect'
import type { AdminFetch } from './AdminCustomers'
import { AdminSupportConversation, type SupportDetail } from './AdminSupportConversation'
import { channelLabels, filterLabels, type SupportChannel, type SupportFilter } from './adminSupportText'

export type SupportRow = {
  id: string; channel: SupportChannel; status: 'OPEN' | 'CLOSED'; category?: string | null; needsHuman: boolean; subject?: string | null
  assignedTo?: number | null; assigneeName?: string | null; createdAt: string; lastCustomerMessageAt?: string | null; closedAt?: string | null
  contactLabel: string; contactName?: string | null; preview?: string | null; hasDraft: boolean; unanswered: boolean; followUp?: boolean
}
type SupportPage = { items: SupportRow[]; page: number; hasMore: boolean }

const when = new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short' })

/** What a row waits for, in the order a person should take it. */
function attention(row: SupportRow) {
  if (row.status === 'CLOSED') return null
  if (row.hasDraft) return { tone: 'attention', text: 'Borrador por aprobar' }
  if (row.needsHuman) return { tone: 'lapsed', text: 'Pide una persona' }
  if (row.followUp) return { tone: 'attention', text: 'Seguimiento pendiente' }
  if (row.unanswered) return { tone: 'attention', text: 'Sin responder' }
  return null
}

/** The support inbox: every conversation by channel, those waiting for a person first, each opened to answer. */
export function AdminSupport({ adminFetch, describeError }: { adminFetch: AdminFetch; describeError: (cause: unknown, fallback: string) => string }) {
  const [filter, setFilter] = useState<SupportFilter>('REVIEW')
  const [channel, setChannel] = useState<SupportChannel | ''>('')
  const [rows, setRows] = useState<SupportRow[]>([])
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [detail, setDetail] = useState<SupportDetail | null>(null)
  const [opening, setOpening] = useState<string | null>(null)
  const [detailError, setDetailError] = useState('')
  const lastLoad = useRef(0)

  const load = useCallback(async (status: SupportFilter, only: SupportChannel | '', nextPage = 0) => {
    const ticket = ++lastLoad.current
    setLoading(true); setError('')
    try {
      const result = await adminFetch<SupportPage>(`/admin/support/conversations?status=${status}&channel=${only}&page=${nextPage}`)
      if (ticket !== lastLoad.current) return
      setRows((current) => nextPage === 0 ? result.items : [...current, ...result.items])
      setPage(result.page); setHasMore(result.hasMore)
    } catch (cause) {
      if (ticket === lastLoad.current) setError(describeError(cause, 'No pudimos leer las conversaciones de soporte.'))
    } finally {
      if (ticket === lastLoad.current) setLoading(false)
    }
  }, [adminFetch, describeError])

  useEffect(() => { void load(filter, channel) }, [filter, channel, load])

  async function open(id: string) {
    setOpening(id); setDetailError('')
    try {
      setDetail(await adminFetch<SupportDetail>(`/admin/support/conversations/${id}`))
    } catch (cause) { setDetailError(describeError(cause, 'No pudimos abrir la conversación.')) } finally { setOpening(null) }
  }

  /** A conversation answered, closed or opened again leaves a filtered list it no longer belongs to. */
  function changed(updated: SupportDetail) {
    setDetail(updated)
    void load(filter, channel)
  }

  return <section className="admin-customers admin-support" aria-labelledby="admin-support-heading">
    <div className="admin-section-heading"><div><h2 id="admin-support-heading">Soporte</h2><p>Lo que escriben clientes y prospectos por el chat del portal. Lo que espera a una persona va primero; abre una conversación para responder.</p></div></div>
    <div className="admin-list-controls">
      <div className="segmented" role="group" aria-label="Estado de las conversaciones">{(Object.keys(filterLabels) as SupportFilter[]).map((value) => <button key={value} type="button" aria-pressed={filter === value} onClick={() => setFilter(value)}>{filterLabels[value]}</button>)}</div>
      <NativeSelect label="Canal" value={channel} onChange={(event) => setChannel(event.target.value as SupportChannel | '')}>
        <option value="">Todos los canales</option>
        {(Object.keys(channelLabels) as SupportChannel[]).map((value) => <option key={value} value={value}>{channelLabels[value]}</option>)}
      </NativeSelect>
    </div>
    {error && rows.length > 0 && <Notice>{error}</Notice>}
    {loading && rows.length === 0 ? <LoadingState label="Leyendo las conversaciones…" />
      : error && rows.length === 0 ? <ErrorState title={error} retry={() => void load(filter, channel)} />
      : rows.length === 0 ? <EmptyState title={filter === 'REVIEW' ? 'Nada espera a una persona' : 'No hay conversaciones'} description="Cuando alguien escriba por el chat del portal, su conversación aparecerá aquí." />
      : <div className="price-table-wrap admin-support-table"><table>
        <thead><tr><th><span className="sr-only">Acciones</span></th><th>Última actividad</th><th>Canal</th><th>Contacto</th><th>Conversación</th><th>Espera</th></tr></thead>
        <tbody>{rows.map((row) => {
          const waits = attention(row)
          return <tr key={row.id} aria-current={detail?.conversation.id === row.id ? 'true' : undefined}>
            <td><Button emphasis="outline" intent="neutral" busy={opening === row.id} onClick={() => void open(row.id)}>Abrir</Button></td>
            <td>{when.format(new Date(row.lastCustomerMessageAt ?? row.createdAt))}{row.status === 'CLOSED' && <span className="admin-customers-sub">Cerrada</span>}</td>
            <td>{channelLabels[row.channel]}</td>
            <td><strong>{row.contactName ?? row.contactLabel}</strong>{row.contactName && <span className="admin-customers-sub">{row.contactLabel}</span>}</td>
            <td className="admin-support-preview">{row.preview ?? row.subject ?? '—'}</td>
            <td>{waits ? <span className="status-chip" data-tone={waits.tone}>{waits.text}</span> : <span className="field-hint">{row.assigneeName ? `Con ${row.assigneeName}` : 'Al día'}</span>}</td>
          </tr>
        })}</tbody>
      </table></div>}
    {hasMore && <div className="admin-customers-more"><Button emphasis="outline" intent="neutral" busy={loading} onClick={() => void load(filter, channel, page + 1)}>Ver más</Button></div>}
    {detailError && <Notice>{detailError}</Notice>}
    {detail && <AdminSupportConversation detail={detail} adminFetch={adminFetch} describeError={describeError} onChanged={changed} />}
  </section>
}
